import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormControl, Select as MuiSelect, MenuItem } from "@mui/material";
import { useLocation } from "react-router-dom";
import { setAvailableModulesForCompany } from "../store/slices/moduleSlice";
import { setFilters } from "../store/slices/filterSlice";
import { resetAllHomeData } from "../store/slices/homeSlice";
import { resetReportYards } from "../store/slices/reportSlice";
import { getAllCompanies } from "../pages/Company/api";
import { VISO_SUPER_ADMIN } from "../constants";

const HIDE_DROPDOWN_PATHS = [
  "/company",
  "/container-lookup",
  "/public-report-details",
];

const CompanyHeader = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const filters = useSelector((state) => state.filters);
  const role = localStorage.getItem("role_name");
  const isVisoAdmin = role === VISO_SUPER_ADMIN;
  const localCompanyId = localStorage.getItem("company_id");

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(
    filters.selectedCompanyId || localCompanyId || "",
  );

  const isCompanyPage = HIDE_DROPDOWN_PATHS.some((path) =>
    location.pathname.startsWith(path),
  );

  const currentCompanyName =
    companies.find((c) => c.company_id === selectedCompanyId)?.company_name ||
    localStorage.getItem("company_name") ||
    "";

  useEffect(() => {
    if (!isVisoAdmin) return;
    const fetchCompanies = async () => {
      try {
        const res = await getAllCompanies();
        if (res.status && res.data) {
          setCompanies(res.data);

          // localCompanyId now reflects the last switched company too,
          // since handleCompanyChange persists it to localStorage.
          const defaultId = filters.selectedCompanyId || localCompanyId;
          if (defaultId) {
            setSelectedCompanyId(defaultId);

            const matchedCompany = res.data.find(
              (c) => c.company_id === defaultId,
            );
            const modules = matchedCompany?.available_modules?.length
              ? matchedCompany.available_modules
              : ["YARD"];

            // NEW — always rehydrate modules for the resolved company on load/refresh,
            // instead of relying only on whatever was set at original login time.
            dispatch(setAvailableModulesForCompany(modules));
            localStorage.setItem("available_modules", JSON.stringify(modules));

            if (!filters.selectedCompanyId) {
              dispatch(
                setFilters({ ...filters, selectedCompanyId: defaultId }),
              );
            }
          }
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisoAdmin]);

  useEffect(() => {
    if (
      filters.selectedCompanyId &&
      filters.selectedCompanyId !== selectedCompanyId
    ) {
      setSelectedCompanyId(filters.selectedCompanyId);
    }
  }, [filters.selectedCompanyId]);

  const handleCompanyChange = (companyId) => {
    setSelectedCompanyId(companyId);

    const company = companies.find((c) => c.company_id === companyId);

    const modules = company?.available_modules?.length
      ? company.available_modules
      : ["YARD"];

    localStorage.setItem("company_id", companyId);
    localStorage.setItem("company_name", company?.company_name || "");
    localStorage.setItem("available_modules", JSON.stringify(modules));

    dispatch(setAvailableModulesForCompany(modules));

    dispatch(resetAllHomeData());
    dispatch(resetReportYards());

    dispatch(
      setFilters({
        ...filters,
        selectedCompanyId: companyId,
        yards: [],
        shippingLines: [],
        // FIX: was startDate: "", endDate: "" — functionally equivalent
        // (both falsy) but inconsistent with filterSlice's own null
        // convention for "no date set yet". Aligned here so every place
        // that clears dates does it the same way.
        startDate: null,
        endDate: null,
        filterMode: "date",
        currentPage: 1,
      }),
    );
  };

  // ✅ All hooks above — early returns below
  // Only show for viso super admin
  if (!isVisoAdmin) return null;
  // Don't show on company pages
  if (isCompanyPage) return null;

  return (
    <div
      style={{
        width: "100%",
        background: "#ffffff",
        borderBottom: "1px solid #e9edf5",
        padding: "0 32px",
        height: 56,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        boxSizing: "border-box",
        flexShrink: 0,
      }}
    >
      {/* ── Left: breadcrumb ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#94a3b8"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 21h18M3 7v14M21 7v14M6 3h12l3 4H3L6 3zM9 21v-6h6v6" />
        </svg>

        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "#94a3b8",
            textTransform: "uppercase",
            letterSpacing: "0.07em",
          }}
        >
          Company
        </span>

        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#cbd5e1"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M9 18l6-6-6-6" />
        </svg>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            borderRadius: 7,
            padding: "4px 12px",
          }}
        >
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "#3b82f6",
              flexShrink: 0,
              display: "inline-block",
            }}
          />
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1d4ed8" }}>
            {currentCompanyName || "—"}
          </span>
        </div>
      </div>

      {/* ── Right: switch company ── */}
      {companies.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#94a3b8",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
            }}
          >
            Switch
          </span>

          <FormControl size="small">
            <MuiSelect
              value={selectedCompanyId}
              onChange={(e) => handleCompanyChange(e.target.value)}
              displayEmpty
              sx={{
                height: 36,
                minWidth: 170,
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: 600,
                background: "#fff",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e2e8f0",
                  borderWidth: "1.5px",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#3b82f6",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#3b82f6",
                  borderWidth: "1.5px",
                },
                "& .MuiSelect-select": {
                  padding: "0 12px",
                  display: "flex",
                  alignItems: "center",
                  height: "36px !important",
                  color: "#1e293b",
                  fontWeight: 600,
                  fontSize: 13,
                  boxSizing: "border-box",
                },
                "& .MuiSvgIcon-root": { color: "#64748b" },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                    mt: 0.5,
                  },
                },
              }}
            >
              <MenuItem
                value=""
                disabled
                sx={{ fontSize: 13, color: "#94a3b8" }}
              >
                Select company…
              </MenuItem>
              {companies.map((c) => (
                <MenuItem
                  key={c.company_id}
                  value={c.company_id}
                  sx={{
                    fontSize: 13,
                    fontWeight: c.company_id === selectedCompanyId ? 700 : 400,
                    color:
                      c.company_id === selectedCompanyId
                        ? "#2563eb"
                        : "#1e293b",
                    "&:hover": { background: "#eff6ff" },
                    "&.Mui-selected": {
                      background: "#eff6ff",
                      "&:hover": { background: "#dbeafe" },
                    },
                  }}
                >
                  {c.company_name}
                </MenuItem>
              ))}
            </MuiSelect>
          </FormControl>
        </div>
      )}
    </div>
  );
};

export default CompanyHeader;